import _ from "lodash";
import PropTypes from "prop-types";
import React, { useState } from "react";

import { Button } from "@toddle-edu/ds-web";

import { truncate } from "Utils";
import I18nHOC from "UIComponents/I18nHOC";

import classes from "./SeeMoreLessHtml.scss";

const getHtmlWithLimitedMedia = source => {
  const iframeRegex = new RegExp(/(<iframe.+?<\/iframe>)/g);
  const iframeMatches = source.match(iframeRegex);
  let iframeIndex, iframeEndIndex, hasIframe;
  if (iframeMatches === null) {
    hasIframe = false;
    iframeIndex = -1;
  } else {
    hasIframe = true;
    iframeIndex = source.indexOf(iframeMatches[0]);
    iframeEndIndex = iframeIndex + _.get(iframeMatches, "[0].length", 0);
  }

  const imgRegex = new RegExp(/(<img[^>]+>)/g);
  const imgMatches = source.match(imgRegex);
  let imgIndex, imgEndIndex, hasImage;
  if (imgMatches === null) {
    hasImage = false;
    imgIndex = -1;
  } else {
    hasImage = true;
    imgIndex = source.indexOf(imgMatches[0]);
    imgEndIndex = imgIndex + _.get(imgMatches, "[0].length", 0);
  }

  const clippedIframeSource = source.slice(0, iframeEndIndex);
  const clippedImageSource = source.slice(0, imgEndIndex);

  if (hasImage && hasIframe)
    return imgIndex < iframeIndex ? clippedImageSource : clippedIframeSource;
  if (hasImage && imgIndex > iframeIndex) return clippedImageSource;
  if (hasIframe && iframeIndex > imgIndex) return clippedIframeSource;
  return source;
};

const SeeMoreLessHtml = props => {
  const {
    t,
    fullSource,
    truncateOptions,
    truncateLength,
    seeMoreText,
    seeLessText,
    onToggle,
    containerStyle,
    seeMoreLessTextStyle,
    shouldLimitEmbeddedMedia,
  } = props;

  const { html: truncatedSource, isTruncated } = truncate(
    fullSource,
    truncateLength,
    truncateOptions
  );

  const collapsedSource = shouldLimitEmbeddedMedia
    ? getHtmlWithLimitedMedia(truncatedSource)
    : truncatedSource;

  const truncationDisabled = !isTruncated;

  const [content, setContent] = useState(collapsedSource);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    if (isExpanded) {
      setIsExpanded(false);
      setContent(collapsedSource);
    } else {
      setIsExpanded(true);
      setContent(fullSource);
    }
    onToggle({ isExpanded: !isExpanded }); // state update is async
  };

  const getSeeMoreLessText = () => {
    if (isExpanded) {
      return seeLessText || t("common:see_less_noDots");
    }
    return seeMoreText || t("common:see_more_noDots");
  };

  const renderProcessedSource = () => {
    return (
      <div
        className={classes.container}
        style={containerStyle}
        dangerouslySetInnerHTML={{ __html: content || "" }}
      />
    );
  };

  return (
    <div>
      {renderProcessedSource()}
      {!truncationDisabled ? (
        <Button
          dsVersion="2.0"
          type="inline"
          variant="progressive"
          onClick={toggleExpand}
          style={seeMoreLessTextStyle}
        >
          {getSeeMoreLessText()}
        </Button>
      ) : null}
    </div>
  );
};

SeeMoreLessHtml.defaultProps = {
  truncateLength: 400,
  truncateOptions: {
    byWords: false,
    ellipsis: "...",
    stripTags: false,
    decodeEntities: false,
    keepWhitespaces: true,
    reserveLastWord: false,
  },
  onToggle: () => {},
  seeMoreLessTextStyle: {},
  constainerStyle: {},
  shouldLimitEmbeddedMedia: false,
};

SeeMoreLessHtml.propTypes = {
  fullSource: PropTypes.string.isRequired,
  truncateLength: PropTypes.number.isRequired,
  truncateOptions: PropTypes.object,
  seeMoreText: PropTypes.string,
  seeLessText: PropTypes.string,
  onToggle: PropTypes.func,
  seeMoreLessTextStyle: PropTypes.object,
  containerStyle: PropTypes.object,
  shouldLimitEmbeddedMedia: PropTypes.bool,
};

SeeMoreLessHtml.displayName = "SeeMoreLessHtml";

export default I18nHOC({ resource: ["common"] })(SeeMoreLessHtml);
